import { Button } from "@/components/ui/Button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { Tooltip } from "@/components/ui/Tooltip";
import { Info } from "lucide-react";
import { StatusBar, StatusCorner } from "./StatusBadge";

const CELL_MARKS: { status: "explained" | "rejected"; name: string; meaning: string }[] = [
  {
    status: "explained",
    name: "Explained",
    meaning:
      "worked out from the paper (for example, calculated), or cited from a source with no text lines, so not checked automatically",
  },
  { status: "rejected", name: "Rejected", meaning: "the cited text does not support the value" },
];

const SAMPLE = "relative mt-0.5 inline-block h-4 w-8 shrink-0 border";

/** A button that opens what the colored marks of the grid mean, and how to select cells. */
export const GridLegend = ({ labelClassName }: { labelClassName?: string }) => (
  <Popover>
    <Tooltip content="What the marks of the grid mean">
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Info className="h-3.5 w-3.5" aria-hidden />
          <span className={labelClassName}>Legend</span>
        </Button>
      </PopoverTrigger>
    </Tooltip>
    <PopoverContent align="end" className="w-80 p-3">
      <ul aria-label="What the status marks mean" className="space-y-2 text-xs text-zinc-500">
        {CELL_MARKS.map(({ status, name, meaning }) => (
          <li key={status} className="flex items-start gap-2">
            <span
              aria-hidden
              className={`${SAMPLE} ${
                status === "rejected" ? "border-red-200 bg-red-50" : "border-zinc-200 bg-white"
              }`}
            >
              <StatusCorner status={status} />
            </span>
            <span>
              <span className="font-medium text-zinc-700">{name}:</span> {meaning}
            </span>
          </li>
        ))}
        <li className="flex items-start gap-2">
          <span aria-hidden className={`${SAMPLE} border-zinc-200 bg-emerald-50`}>
            <StatusBar status="verified" />
          </span>
          <span>
            <span className="font-medium text-zinc-700">Row status:</span> the left edge of
            the row name is green when all its filled cells pass, and red when any cell is
            rejected.
          </span>
        </li>
      </ul>
      <p className="mt-3 border-t border-zinc-200 pt-2 text-xs leading-5 text-zinc-500">
        Click a cell to select it. Shift-click, Ctrl-click, or drag to select more cells.
        Right-click or double-click a cell or header for details.
      </p>
    </PopoverContent>
  </Popover>
);
